import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Home.css";
import logo from "../assets/General/logo.png";
import languages from "../assets/Home/languages.png";
import traditions from "../assets/Home/traditions.png";
import clothes from "../assets/Home/clothes.png";
import food from "../assets/Home/food.png";
import animals from "../assets/Home/animals.png";
import clothesBadge from "../assets/badges/clothesbadge.png";
import foodBadge from "../assets/badges/foodbadge.png";
import languagesBadge from "../assets/badges/languagesbadge.png";
import landmarksBadge from "../assets/badges/landmarksbadge.png";
import animalsBadge from "../assets/badges/animalbadge.png";


const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// maps badge keys to their corresponding badge images
const BADGE_IMAGES = {
  clothes_master:   clothesBadge,
  food_master:      foodBadge,
  languages_master: languagesBadge,
  landmarks_master: landmarksBadge,
  animals_master:   animalsBadge,
};

// human-readable label for each badge shown inside the badges modal
const BADGE_LABELS = {
  clothes_master:   "Clothes Master Badge",
  food_master:      "Food Master Badge",
  landmarks_master: "Landmarks Master Badge",
  languages_master: "Languages Master Badge",
  animals_master:   "Animals Master Badge",
};

// the five topic cards shown in the carousel on the homescreen
const TOPIC_CARDS = [
  {
    name: "Food",
    description: "Let's get a taste of the wonderful Sri Lankan Cuisine",
    image: food,
    to: "/food",
    quizTo: "/FoodQuiz",
    badgeKey: "food_master",
  },
  {
    name: "Landmarks",
    description: "Let's explore the wonderful landmarks of Sri Lanka",
    image: traditions,
    to: "/landmarks",
    quizTo: "/landmarksquiz",
    badgeKey: "landmarks_master",
  },
  {
    name: "Clothing",
    description: "Let's take a look at the types of clothing worn in Sri Lanka",
    image: clothes,
    to: "/clothing",
    quizTo: "/ClothesQuiz",
    badgeKey: "clothes_master",
  },
  {
    name: "Languages",
    description: "Let's learn the national languages of Sri Lanka",
    image: languages,
    to: "/languages",
    quizTo: "/languagesquiz",
    badgeKey: "languages_master",
  },
  {
    name: "Animals",
    description: "Let's explore the beautiful animals in Sri Lanka",
    image: animals,
    to: "/animals",
    quizTo: "/animalsquiz",
    badgeKey: "animals_master",
  },
];

//slick carousel config, shows 3 slides and drops to 3 on smaller breakpoints too
const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  arrows: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
      },
    },
  ],
};

function getUserName() {
  const possibleKeys = [
    "exploreLankaUser",
    "exploreLankaUsername",
    "currentUser",
    "user",
    "username",
    "name",
  ];

  for (const key of possibleKeys) {
    const stored = localStorage.getItem(key);
    if (!stored) continue;

    try {
      const parsed = JSON.parse(stored);
      if (typeof parsed === "string" && parsed.trim()) return parsed.trim();
      if (parsed?.name) return String(parsed.name).trim();
      if (parsed?.user?.name) return String(parsed.user.name).trim();
      if (parsed?.username) return String(parsed.username).trim();
    } catch {
      // if it wasnt valid json just use the raw string
      if (stored.trim()) return stored.trim();
    }
  }

  return "";
}

// builds the localStorage key used to track which badges the player has already seen
function getSeenBadgesStorageKey(playerName) {
  return `exploreLankaSeenBadges:${playerName.toLowerCase()}`;
}

export default function Home() {
  const navigate = useNavigate();

  //tthe full list of badge objects returned from the api
  const [ownedBadges, setOwnedBadges] = useState([]);

  // set of badge_key strigns for quick membership checks on the cards
  const [ownedBadgeKeys, setOwnedBadgeKeys] = useState(new Set());

  // controls wether the badges modal is open
  const [showBadgesModal, setShowBadgesModal] = useState(false);

  // true when the player has badges they havent viewed yet, triggers the glow animaton
  const [hasUnseenBadges, setHasUnseenBadges] = useState(false);

  // clears storage and sends the player back to the login screen
  function handleLogout() {
    localStorage.clear();
    navigate("/");
  }

  useEffect(() => {
    async function fetchBadges() {
      const playerName = getUserName();
      if (!playerName) return;

      try {
        const response = await fetch(
          `${API_BASE}/api/badges/${encodeURIComponent(playerName)}`
        );

        let result = {};
        try {
          result = await response.json();
        } catch {
          result = {};
        }

        if (!response.ok) {
          console.error("[Home] failed to fetch badges:", result);
          return;
        }

        const badges = Array.isArray(result?.badges) ? result.badges : [];
        const badgeKeys = new Set(
          badges.map((badge) => badge.badge_key).filter(Boolean)
        );

        setOwnedBadges(badges);
        setOwnedBadgeKeys(badgeKeys);

        // compare owned badges against the ones already stored as seen
        const seenKey = getSeenBadgesStorageKey(playerName);
        const seenBadges = new Set(
          JSON.parse(localStorage.getItem(seenKey) || "[]")
        );

        const unseenExists = badges.some(
          (badge) => badge?.badge_key && !seenBadges.has(badge.badge_key)
        );

        setHasUnseenBadges(unseenExists);
      } catch (error) {
        console.error("[Home] network error while fetching badges:", error);
      }
    }

    fetchBadges();
  }, []);

  // builds the array of badge card objects shown inside the modal
  //filters out any badges that dont have a matching image in BADGE_IMAGES
  const badgeCards = useMemo(() => {
    return ownedBadges
      .filter((badge) => badge?.badge_key && BADGE_IMAGES[badge.badge_key])
      .map((badge) => ({
        ...badge,
        image: BADGE_IMAGES[badge.badge_key],
        label:
          badge.badge_name ||
          BADGE_LABELS[badge.badge_key] ||
          badge.badge_key,
      }));
  }, [ownedBadges]);

  // opens the modal and marks all current badges as seen in localStorage
  function openBadgesModal() {
    const playerName = getUserName();
    setShowBadgesModal(true);

    if (!playerName) return;

    const seenKey = getSeenBadgesStorageKey(playerName);
    const keysToStore = ownedBadges
      .map((badge) => badge?.badge_key)
      .filter(Boolean);

    localStorage.setItem(seenKey, JSON.stringify(keysToStore));
    setHasUnseenBadges(false);
  }

  function closeBadgesModal() {
    setShowBadgesModal(false);
  }

  return (
    <div className="homepage">
      <img className="logo" src={logo} alt="Explore Lanka Logo" />

      {/* badges button, glows when the player has unseen badges */}
      <button
        type="button"
        className={`badgesButton ${hasUnseenBadges ? "hasNotification" : ""}`}
        onClick={openBadgesModal}
        aria-label="Open badges"
      >
        {/* inline svg badge/ribbon icon */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="badgesButtonIcon"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.39113 10.5439C6.39051 9.44976 6.7016 8.37924 7.28617 7.46394C7.86503 6.55596 8.69417 5.84514 9.6681 5.42194C10.9668 4.85935 12.4322 4.85935 13.7309 5.42194C14.7052 5.84498 15.5347 6.55581 16.1138 7.46394C17.3021 9.32579 17.309 11.731 16.1314 13.5999L18.525 17.9719L16.0037 17.4719L15.1973 19.9999L12.9753 15.9249C12.1382 16.1414 11.2618 16.1414 10.4247 15.9249L8.20267 19.9999L7.39635 17.4709L4.875 17.9719L7.26863 13.5999C6.69465 12.6895 6.38988 11.6281 6.39113 10.5439V10.5439Z"
            stroke="#ffffff"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.4504 8.32691C11.4973 8.22925 11.5943 8.16742 11.7005 8.16742C11.8066 8.16742 11.9036 8.22925 11.9506 8.32691L12.4683 9.37291C12.5069 9.45209 12.5788 9.50881 12.6633 9.52691L13.7475 9.74991C13.8468 9.77396 13.9266 9.84932 13.9583 9.94877C13.99 10.0482 13.9688 10.1574 13.9025 10.2369L13.1284 11.1369C13.0738 11.2009 13.0483 11.2856 13.0582 11.3699L13.2015 12.5879C13.216 12.6944 13.1719 12.8004 13.0868 12.8633C13.0018 12.9262 12.8898 12.9357 12.7959 12.8879L11.8209 12.3799C11.7419 12.3384 11.6483 12.3384 11.5693 12.3799L10.5943 12.8879C10.5004 12.9357 10.3884 12.9262 10.3034 12.8633C10.2183 12.8004 10.1742 12.6944 10.1887 12.5879L10.3321 11.3729C10.3419 11.2886 10.3164 11.2039 10.2619 11.1399L9.4877 10.2399C9.41982 10.159 9.39893 10.0473 9.43286 9.94637C9.46679 9.84548 9.55041 9.77067 9.65248 9.74991L10.7367 9.52591C10.8212 9.50781 10.893 9.45109 10.9317 9.37191L11.4504 8.32691Z"
            stroke="#ffffff"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7.90164 13.1977C7.6795 12.8481 7.21601 12.7448 6.8664 12.9669C6.51679 13.189 6.41345 13.6525 6.63559 14.0021L7.90164 13.1977ZM7.95112 14.4679L8.49338 13.9498L8.49105 13.9474L7.95112 14.4679ZM10.1224 15.8399L10.3567 15.1274L10.3557 15.1271L10.1224 15.8399ZM10.2422 16.6561C10.6433 16.7596 11.0523 16.5184 11.1558 16.1173C11.2593 15.7162 11.018 15.3072 10.6169 15.2037L10.2422 16.6561ZM16.7644 14.0021C16.9865 13.6525 16.8832 13.189 16.5336 12.9669C16.184 12.7448 15.7205 12.8481 15.4983 13.1977L16.7644 14.0021ZM15.4489 14.4679L14.9089 13.9474L14.9066 13.9498L15.4489 14.4679ZM13.2775 15.8399L13.0443 15.1271L13.0433 15.1274L13.2775 15.8399ZM12.783 15.2037C12.382 15.3072 12.1407 15.7162 12.2442 16.1173C12.3477 16.5184 12.7567 16.7596 13.1578 16.6561L12.783 15.2037Z"
            fill="#ffffff"
          />
        </svg>

        {/* red dot shown on the button when there are unseen badges */}
        {hasUnseenBadges && <span className="badgesNotificationDot" />}
      </button>

      {/* logout button, fixed to the top-right corner */}
      <button
        type="button"
        className="logoutButton"
        onClick={handleLogout}
        aria-label="Logout"
      >
        {/* inline svg logout/exit icon */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="logoutButtonIcon"
        >
          <path
            d="M21 12L13 12"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18 15L20.913 12.087V12.087C20.961 12.039 20.961 11.961 20.913 11.913V11.913L18 9"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 5V4.5V4.5C16 3.67157 15.3284 3 14.5 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H14.5C15.3284 21 16 20.3284 16 19.5V19.5V19"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* the main carousel of topic cards */}
      <div className="cards">
        <Slider {...sliderSettings}>
          {TOPIC_CARDS.map((topic) => {
            // only show the badge image on the card if the player actually owns that badge
            const shouldShowBadge =
              topic.badgeKey &&
              ownedBadgeKeys.has(topic.badgeKey) &&
              BADGE_IMAGES[topic.badgeKey];

            return (
              <div key={topic.name}>
                <div
                  className="card"
                  onClick={() => navigate("/loading", { state: { to: topic.to } })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate("/loading", { state: { to: topic.to } });
                    }
                  }}
                >
                  {/* badge image shows in the top-right corner of the card if earned */}
                  {shouldShowBadge && (
                    <img
                      src={BADGE_IMAGES[topic.badgeKey]}
                      alt={`${topic.name} badge`}
                      className="cardBadge"
                    />
                  )}

                  <div className="cardInner">
                    <img className="cardImg" src={topic.image} alt={topic.name} />
                  </div>

                  <h2>{topic.name}</h2>
                  <p>{topic.description}</p>

                  <div className="cardButtons">
                    <button
                      type="button"
                      className="playBtn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/loading", { state: { to: topic.to } });
                      }}
                    >
                      Learn
                    </button>

                    {/* quiz button only renders if the topic has a quiz route */}
                    {topic.quizTo && (
                      <button
                        type="button"
                        className="quizBtn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/loading", { state: { to: topic.quizTo } });
                        }}
                      >
                        Quiz
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </Slider>
      </div>

      {/* badges modal, clicking the overlay closes it */}
      {showBadgesModal && (
        <div className="badgesModalOverlay" onClick={closeBadgesModal}>
          <div className="badgesModal" onClick={(e) => e.stopPropagation()}>
            <div className="badgesModalHeader">
              <h2 className="badgesModalTitle">My Badges</h2>
              <button
                type="button"
                className="badgesModalClose"
                onClick={closeBadgesModal}
              >
                ✕
              </button>
            </div>

            {/* empty state shown if the player hasnt earned any badges yet */}
            {badgeCards.length === 0 ? (
              <p className="badgesEmptyText">
                You have not unlocked any badges yet.
              </p>
            ) : (
              <div className="badgesRow" aria-label="Badge list">
                {badgeCards.map((badge) => (
                  <div key={badge.badge_key} className="badgesRowCard">
                    <img
                      src={badge.image}
                      alt={badge.label}
                      className="badgesRowImage"
                    />
                    <p className="badgesRowLabel">{badge.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}